import { Close, Email, LocationOn, Phone, Share } from '@mui/icons-material';
import {
  Button,
  IconButton,
  Paper,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { Show } from '@side/pantry';
import { useMap } from '@vis.gl/react-google-maps';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import {
  anchoredPartnerCardId,
  selectedGeocodeLocation,
  selectedGroupKey,
  selectedPartnerMapTeamId,
  teamDetailPanelVisible,
  viewAllTeamId,
} from '@/common/atoms/community';
import { FullSizeFlexContainer } from '@/components/FullSizeFlexContainer';
import { Spinner } from '@/components/Spinner';
import { TXM_REFERRALS_PATH } from '@/constants';
import { isMobileOperatingSystem } from '@/helpers/isMobileOperatingSystem/isMobileOperatingSystem';
import { TrackCommunityEvent } from '$analytics/useTrackAnalytics/domains/community/events';
import { useTrackCommunityAnalytics } from '$analytics/useTrackAnalytics/domains/community/useTrackCommunityAnalytics';
import { useGetPartnerInformation } from '../../hooks/useGetPartnerInformation';
import { useGetTeamOrganization } from '../../hooks/useGetTeamOrganization';
import { useShowAllTeamLocations } from '../../hooks/useShowAllTeamLocations';
import { computeViewAllTeamsBounds } from '../../hooks/useShowAllTeamLocations/helpers';
import { matches } from '../../utils/location';
import { CorrectInformationText } from '../CorrectInformationText';
import { TeamLogo } from '../TeamLogo/TeamLogo';
import { InformationItem } from './InformationItem';
import { PartnerAgentsList } from './PartnerAgentsList';
import { PartnerDetailsWrapper } from './PartnerDetailsWrapper';
import { TeamDescription } from './TeamDescription';
import { TeamWebsiteItem } from './TeamWebsiteItem';

const CLOSE_BUTTON_SIZE = 40;
const STICKY_HEADER_TOP = 8;

const StickyHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: 0,
  paddingRight: theme.spacing(1),
  marginTop: `${STICKY_HEADER_TOP}px`,
  marginBottom: `${-1 * CLOSE_BUTTON_SIZE - STICKY_HEADER_TOP}px`,
  textAlign: 'right',
  '&.stuck': {
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    zIndex: 1,
  },
}));

export const PartnerDetailsPanel = () => {
  const trackEvent = useTrackCommunityAnalytics();
  const isMobileOS = isMobileOperatingSystem();
  const map = useMap();
  const [teamDetailsOpen, setTeamDetailsOpen] = useAtom(teamDetailPanelVisible);
  const setSelectedGroupKey = useSetAtom(selectedGroupKey);
  const selectedTeamId = useAtomValue(selectedPartnerMapTeamId);
  const selectedViewAllTeamId = useAtomValue(viewAllTeamId);
  const { teams: orgTeams } = useGetTeamOrganization(selectedViewAllTeamId);
  const selectedLocation = useAtomValue(selectedGeocodeLocation);
  const setAnchoredPartnerId = useSetAtom(anchoredPartnerCardId);
  const { partnerDetails } = useGetPartnerInformation({
    teamId: selectedTeamId,
  });
  const showAllTeamLocationsCallback = useShowAllTeamLocations();
  const [headerStuck, setHeaderStuck] = useState(false);
  const [viewAllTeamsButtonDisabled, setViewAllTeamsButtonDisabled] =
    useState(false);

  const {
    formattedAddress,
    legalName,
    logoUri,
    websiteUrl,
    email,
    phoneNumber,
    leadAgents,
    alias,
    description,
    id,
    mailingAddress,
    topLevelTeamId,
  } = partnerDetails ?? {};

  const newReferralUrl =
    leadAgents?.length === 1
      ? `${TXM_REFERRALS_PATH}/new?teamId=${id}&userId=${leadAgents[0].id}`
      : `${TXM_REFERRALS_PATH}/new`;

  const trackingEventPayload = {
    teamId: id,
    teamAddressId: mailingAddress?.id,
    inBoundary:
      selectedLocation && mailingAddress
        ? matches(selectedLocation, mailingAddress)
        : null,
  };

  const handleClose = () => {
    setTeamDetailsOpen(false);
    setSelectedGroupKey(undefined);
    setAnchoredPartnerId(undefined);
    trackEvent(
      TrackCommunityEvent.PartnerMapTeamDetailsClose,
      trackingEventPayload,
    );
  };

  useEffect(() => {
    if (!teamDetailsOpen) {
      setHeaderStuck(false);
    }
  }, [teamDetailsOpen]);

  useEffect(() => {
    if (!map || !selectedViewAllTeamId || !orgTeams) {
      return;
    }

    // Bounds are fixed for a given org, so compute them once and let the
    // listener just test containment as the user pans/zooms.
    const teamBounds = computeViewAllTeamsBounds(orgTeams);
    const listener = map.addListener('bounds_changed', () => {
      const allTeamsInView =
        teamBounds &&
        map.getBounds()?.contains(teamBounds.getNorthEast()) &&
        map.getBounds()?.contains(teamBounds.getSouthWest());
      setViewAllTeamsButtonDisabled(!!allTeamsInView);
    });

    return () => {
      listener.remove();
    };
  }, [orgTeams, map, selectedViewAllTeamId]);

  return (
    <PartnerDetailsWrapper teamId={id}>
      <Show
        when={!!partnerDetails}
        fallback={<Spinner centered sx={{ height: 300 }} />}
      >
        <FullSizeFlexContainer
          sx={{ backgroundColor: 'common.white' }}
          onScroll={(e) =>
            setHeaderStuck(
              (e.target as HTMLElement).scrollTop >= STICKY_HEADER_TOP,
            )
          }
        >
          <StickyHeader className={headerStuck ? 'stuck' : ''}>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </StickyHeader>
          <Stack direction="column" sx={{ gap: 3, p: 2 }}>
            <Stack
              direction="row"
              sx={{
                alignItems: 'start',
                gap: 2,
                justifyContent: 'space-between',
              }}
            >
              <TeamLogo
                logoUri={logoUri}
                legalName={legalName}
                sx={{
                  maxHeight: 80,
                  maxWidth: 200,
                  flexShrink: 1,
                  overflow: 'hidden',
                }}
                defaultLogoWidth={118}
              />
              {topLevelTeamId && (
                <Button
                  size="small"
                  variant="text"
                  disabled={viewAllTeamsButtonDisabled}
                  onClick={() => {
                    trackEvent(
                      TrackCommunityEvent.PartnerMapTeamDetailsViewAllLocationsClick,
                      {
                        eventTarget: 'all_locations_button',
                        ...trackingEventPayload,
                      },
                    );
                    void showAllTeamLocationsCallback(topLevelTeamId);
                  }}
                  sx={{
                    height: CLOSE_BUTTON_SIZE,
                    mr: `${CLOSE_BUTTON_SIZE}px`,
                    mt: -1,
                    flexShrink: 0,
                  }}
                >
                  View All Locations
                </Button>
              )}
            </Stack>
            <Stack direction="column" sx={{ gap: 1.5 }}>
              <Typography component="h1" variant="h3">
                {legalName}
              </Typography>
              {alias && (
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'text.secondary' }}
                >
                  {alias}
                </Typography>
              )}
            </Stack>
            <Stack direction="column" sx={{ gap: 2 }}>
              <InformationItem content={formattedAddress} Icon={LocationOn} />
              <TeamWebsiteItem
                website={websiteUrl}
                onClick={() => {
                  trackEvent(
                    TrackCommunityEvent.PartnerMapTeamDetailsActionButtonClick,
                    {
                      eventTarget: 'website',
                      ...trackingEventPayload,
                    },
                  );
                }}
              />
              <InformationItem
                content={phoneNumber}
                Icon={Phone}
                copyable
                copyButtonId="partner-copy-phone"
                onCopy={() =>
                  trackEvent(TrackCommunityEvent.PartnerMapTeamDetailsCopy, {
                    eventTarget: 'phone_number',
                    ...trackingEventPayload,
                  })
                }
              />
              <InformationItem
                content={email}
                Icon={Email}
                copyable
                copyButtonId="partner-copy-email"
                onCopy={() =>
                  trackEvent(TrackCommunityEvent.PartnerMapTeamDetailsCopy, {
                    eventTarget: 'email',
                    ...trackingEventPayload,
                  })
                }
              />
              <PartnerAgentsList
                leadAgents={leadAgents}
                pb={description ? 4 : 6}
              />
              <TeamDescription description={description} />
              <CorrectInformationText
                leadAgents={leadAgents}
                onClick={() => {
                  trackEvent(
                    TrackCommunityEvent.PartnerMapTeamDetailsAccountSettingsClick,
                    {
                      teamId: id,
                      teamAddressId: mailingAddress?.id,
                      hasBio: !!description,
                    },
                  );
                }}
              />
            </Stack>
          </Stack>
        </FullSizeFlexContainer>
        <Paper elevation={3}>
          <Stack spacing={2} sx={{ p: 3 }}>
            <Stack direction="row" spacing={2}>
              {isMobileOS && phoneNumber && (
                <Button
                  id="partner-call-link"
                  variant="outlined"
                  href={`tel:${phoneNumber}`}
                  sx={{ flex: '1 1 0px' }}
                  onClick={() => {
                    trackEvent(
                      TrackCommunityEvent.PartnerMapTeamDetailsActionButtonClick,
                      {
                        eventTarget: 'call_button',
                        ...trackingEventPayload,
                      },
                    );
                  }}
                >
                  <Phone sx={{ mr: 1 }} />
                  Call
                </Button>
              )}
              <Button
                id="partner-referral-link"
                variant="outlined"
                LinkComponent={Link}
                href={newReferralUrl}
                onClick={() => {
                  trackEvent(
                    TrackCommunityEvent.PartnerMapTeamDetailsActionButtonClick,
                    {
                      eventTarget: 'referral_button',
                      ...trackingEventPayload,
                    },
                  );
                }}
                sx={{ flex: '1 1 0px' }}
              >
                <Share sx={{ mr: 1 }} />
                Refer a Client
              </Button>
            </Stack>
            {email && (
              <Button
                id="partner-email-link"
                variant="outlined"
                href={`mailto:${email}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent(
                    TrackCommunityEvent.PartnerMapTeamDetailsActionButtonClick,
                    {
                      eventTarget: 'email_button',
                      ...trackingEventPayload,
                    },
                  );
                }}
              >
                <Email sx={{ mr: 1 }} />
                Email
              </Button>
            )}
          </Stack>
        </Paper>
      </Show>
    </PartnerDetailsWrapper>
  );
};
